import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'

export const useCompanyArbitrationStore = defineStore('company-arbitration', () => {
  const suiteStore = useSuiteStore()

  const isLoadingArbitrations = ref(false)
  const arbitrations = ref<any[]>([])
  const pendingCounts = ref<number | null>(null)
  const total = ref(0)
  const totalPages = ref(1)
  const currentArbitration = ref<any | null>(null)
  const statusCounts = ref<Record<string, number>>({
    PENDING: 0,
    APPROVED: 0,
    REJECTED: 0,
  })

  const fetchCheckArbitration = async (orderUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`company-arbitration/check?orderUuid=${encodeURIComponent(orderUuid)}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        currentArbitration.value = await response.json()
        return currentArbitration.value
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'arbitrage:', error)
    }
    return null
  }

  const clearCurrentArbitration = () => {
    currentArbitration.value = null
  }

  const fetchArbitration = async (arbitrationUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`company-arbitration/${arbitrationUuid}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'arbitrage:', error)
    }
    return null
  }

  const fetchByCompany = async (companyUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`company-arbitration/by-company/${companyUuid}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des arbitrages de la société:', error)
    }
    return []
  }

  const submitListPayload = (options: {
    page?: number
    pageSize?: number
    status?: string
    orderBy?: string
    order?: string
    search?: string
    createdAt?: any
  }) => {
    const payload: any = {
      page: options.page,
      pageSize: options.pageSize,
      status: options.status,
      orderBy: options.orderBy,
      order: options.order,
    }
    if (options.search) payload.search = options.search
    if (options.createdAt) payload.createdAt = options.createdAt
    return payload
  }

  const applyListResponse = async (response: any, pageSize: number) => {
    if (response && typeof response !== 'boolean' && response.ok) {
      const data = await response.json()
      arbitrations.value = Array.isArray(data.data) ? data.data : []
      pendingCounts.value = typeof data.totalPending === 'number' ? data.totalPending : null
      total.value = typeof data.total === 'number' ? data.total : arbitrations.value.length
      totalPages.value =
        typeof data.totalPages === 'number' ? data.totalPages : Math.max(1, Math.ceil(total.value / pageSize))
      if (data.statusCounts) statusCounts.value = data.statusCounts
      return data
    }
    if (response && typeof response !== 'boolean') {
      const errorData = await response.json().catch(() => ({}))
      console.error('Erreur API arbitrages:', errorData.message || `HTTP ${response.status}`)
    }
    return null
  }

  const resetList = () => {
    arbitrations.value = []
    pendingCounts.value = null
    statusCounts.value = { PENDING: 0, APPROVED: 0, REJECTED: 0 }
  }

  const fetchMyArbitrations = async (options: {
    page?: number
    pageSize?: number
    status?: string
    orderBy?: string
    order?: string
    search?: string
    createdAt?: any
  } = {}) => {
    isLoadingArbitrations.value = true
    try {
      const response = await suiteStore.suiteFetch('company-arbitration/me', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitListPayload(options)),
        credentials: 'include',
      })
      const data = await applyListResponse(response, options.pageSize ?? 20)
      if (data) return data
    } catch (error) {
      console.error('Erreur lors de la récupération de mes arbitrages:', error)
    } finally {
      isLoadingArbitrations.value = false
    }
    resetList()
    return null
  }

  const fetchArbitrationsToValidate = async (options: {
    page?: number
    pageSize?: number
    status?: string
    orderBy?: string
    order?: string
    search?: string
    createdAt?: any
  } = {}) => {
    isLoadingArbitrations.value = true
    try {
      const response = await suiteStore.suiteFetch('company-arbitration/to-validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitListPayload(options)),
        credentials: 'include',
      })
      const data = await applyListResponse(response, options.pageSize ?? 20)
      if (data) return data
    } catch (error) {
      console.error('Erreur lors de la récupération des arbitrages à valider:', error)
    } finally {
      isLoadingArbitrations.value = false
    }
    resetList()
    return null
  }

  const createArbitration = async (orderUuid: string, commercialArgument: string) => {
    const response = await suiteStore.suiteFetch('company-arbitration', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderUuid, commercialArgument }),
      credentials: 'include',
    })
    if (response && typeof response !== 'boolean' && response.ok) {
      return await response.json()
    }
    if (response && typeof response !== 'boolean') {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }
    throw new Error('Échec de la création de l\'arbitrage')
  }

  const addArbitrationComment = async (arbitrationUuid: string, text: string) => {
    const response = await suiteStore.suiteFetch(`company-arbitration/${arbitrationUuid}/comment`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
      credentials: 'include',
    })
    if (response && typeof response !== 'boolean' && response.ok) {
      const data = await response.json()
      const index = arbitrations.value.findIndex((a) => a.uuid === arbitrationUuid)
      if (index !== -1) {
        arbitrations.value[index] = { ...arbitrations.value[index], comment: data.comment }
      }
      return data
    }
    if (response && typeof response !== 'boolean') {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }
  }

  const updateArbitrationStatus = async (
    arbitrationUuid: string,
    status: 'APPROVED' | 'REJECTED',
    decisionComment?: string,
  ) => {
    const payload: any = { decision: status }
    if (decisionComment) payload.decisionComment = decisionComment

    const response = await suiteStore.suiteFetch(`company-arbitration/${arbitrationUuid}/decide`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    })
    if (response && typeof response !== 'boolean' && response.ok) {
      const data = await response.json()
      const index = arbitrations.value.findIndex((a) => a.uuid === arbitrationUuid)
      if (index !== -1) {
        arbitrations.value[index] = data
      }
      return data
    }
    if (response && typeof response !== 'boolean') {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }
  }

  return {
    isLoadingArbitrations,
    arbitrations,
    pendingCounts,
    total,
    totalPages,
    currentArbitration,
    statusCounts,
    clearCurrentArbitration,
    fetchCheckArbitration,
    fetchArbitration,
    fetchByCompany,
    fetchMyArbitrations,
    fetchArbitrationsToValidate,
    createArbitration,
    addArbitrationComment,
    updateArbitrationStatus,
  }
})
